<template>
  <div class='password-spray-issue-body'>
    <div class="description-block" :style="`flex-wrap: ${isShowAll ? 'wrap' : 'nowrap'};`">
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container" :class='{closed: !isShowAll}'>
        <div class='description'>{{description}}</div>
        <div class='description-title' v-if="problems !== ''">{{$t('issueDataPopup.employees.problems')}}</div>
        <div class='problems' v-if="problems !== ''">{{problems}}</div>
      </div>
      <span class='hide-technologies-btn' @click.stop="isShowAll = !isShowAll">
          {{ captionOfTechnologiesToggleButton }}
      </span>
    </div>
    <div class="asset-block">
      <div class="data-title">{{$t('detailed.issue.tld.title')}}</div>
      <div class="text-container">
        {{tld}}
      </div>
    </div>
    <div class="tld-block">
      <div class='details-column' v-if='asset'>
        <div class="data-title">{{$t('issueDataPopup.employees.email')}}</div>
        <div class="text-container">
          {{asset}}
        </div>
      </div>
      <div class='details-column' v-if='leak'>
        <div class="data-title" v-if='leak'>{{$t('issueDataPopup.employees.leak.value')}}</div>
        <v-text-field
          :type="showPassword ? 'text' : 'password'"
          :append-icon="showPassword ? 'visibility' : 'visibility_off'"
          @click:append="showPassword = !showPassword"
          :value="leak"
          class="leaked-data-input"
          readonly
        >
        </v-text-field>
      </div>
    </div>
    <div class="technical-block">
      <div class="technical-data">
        <div class="data-sub-title">{{$t('issueDataPopup.password.spray.module.name')}}</div>
        <span class='content' v-if='logoDict[moduleValidated]'>
          <img class='logo' :src="require(`@/assets/icons/systems/${logoDict[moduleValidated]}.svg`)" alt=""/>
        </span>
        <span class='text-container module-validated'>{{ moduleValidated }}</span>
      </div>
      <div class="technical-data">
        <div class="data-sub-title">{{$t('issueDataPopup.password.spray.platform.url')}}</div>
        <a :href='platformUrl' target="_blank"> {{ platformUrl }} </a>
      </div>
    </div>
    <span class="solution-block" v-if='solutions.length > 0'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
        <div v-for='(solution, index) in solutions' :key='index'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
         <div class='text-container mitigation-option__url'>{{solution}}</div>
        </div>
      </div>
    </span>
    <span class="solution-block" v-if='urls.length > 0'>
      <div class="data-title">{{$t('issueDataPopup.employees.url')}}</div>
        <div v-for='(url, index) in urls' :key='index'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
        <a class="mitigation-option__url" :href='url' target="_blank"> {{ url }} </a>
        </div>
      </div>
    </span>
  </div>
</template>

<script>
export default {
  name: 'PasswordSprayIssueBody',
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isShowAll: false,
      showPassword: false,
    };
  },
  computed: {
    logoDict() {
      return {
        aws: 'AWS',
        atlassian: 'Atlassian',
        checkpoint: 'CheckPoint',
        ews: 'EWS',
        fortivpn: 'FortiVPN',
        github: 'Github',
        gitlab: 'Gitlab',
        google: 'Google',
        joomla: 'Joomla',
        msol: 'MSOnline',
        o365: 'Office365',
        okta: 'Okta',
        owa: 'OWA',
        wordpress: 'Wordpress',
      };
    },
    tld() {
      return this.issue.tld;
    },
    asset() {
      return this.issue.asset;
    },
    metaData() {
      return this.issue.metaData;
    },
    description() {
      return this.issue.description;
    },
    moduleValidated() {
      return this.metaData.module;
    },
    platformUrl() {
      return this.metaData.platformUrl?.[0] || this.metaData.platformUrl || '';
    },
    problems() {
      return this.metaData?.problems || '';
    },
    captionOfTechnologiesToggleButton() {
      return this.isShowAll ? this.$t('detailed.issue.show.less.list') : this.$t('detailed.issue.show.more.list');
    },
    solutions() {
      return this.metaData?.mitigation || [];
    },
    leak() {
      return this.metaData.data.leak;
    },
    urls() {
      return this.metaData?.url || [];
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.password-spray-issue-body {
  .logo {
    width: rem(24);
    height: rem(24);
    margin-right: rem(3);
    display: inline-block;
  }
  .details-column {
    display: inline-grid;
    width: fit-content;
    max-width: rem(420);
    margin-right: $space-4;
  }
  .module-validated{
    text-transform: capitalize;
  }
  .description-block {
    display: block;
    margin-bottom: $space-5;
    width: rem(650);
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .description, .explanation {
      margin-bottom: $space-2;
    }
    .text-container {
      padding-right: $space-15;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      white-space: pre-line;
      &.closed {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        line-height: 1.2em;
        height: 3.6em;
      }
      .description-title {
        font-weight: bold;
        font-size: rem(13);
        margin-bottom: $space-2;
      }
    }
  }
  .solution-block {
    display: block;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .mitigation-option {
      display: flex;
      &__url {
        max-width: rem(650);
      }
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
      margin-right: rem(5);
    }
  }
  .technical-block {
    padding-bottom: $space-2;
    display: block;
    .technical-data {
      padding-top: $space-1;
      padding-bottom: $space-2;
    }
  }
  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .asset-block {
    margin-bottom: $space-5;
  }
  .tld-block {
    margin-bottom: $space-2;
  }
  .hide-technologies-btn {
    color: var(--brand-primary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
    letter-spacing: 0;
    &:hover {
      cursor: pointer;
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.password-spray-issue-body {
  .v-icon{
    font-size: rem(18);
    color: var(--text-secondary);
  }
  max-width: 70%;
  .v-text-field > .v-input__control > .v-input__slot:before {
    border-style: none;
  }
  .v-text-field > .v-input__control > .v-input__slot:after {
    border-style: none;
  }
  .v-text-field {
    margin-top: rem(-4);
    padding-top: 0;
  }
}
</style>
